import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Text,
  Tooltip,
  Box,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Date, Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import {
  InputFormField,
  NumberInputFormField,
} from 'packages/common-frontend/components/FormField';
import {
  RecurringDateV2,
  RecurringDateV2Body,
} from 'packages/common-frontend/components/RecurringDateForm/RecurringDateV2';
import { RelativeRecurringDateForm } from 'packages/common-frontend/components/RecurringDateForm/RelativeRecurringDateForm';
import { ToggleIcon } from 'packages/common-frontend/components/ToggleIcon';

const { cloneDeep } = _;

export type GlobalRecurringDateConfig = CreditAgreement.Config.GlobalRecurringDate.Config;
export type RecurringDateSetupType = CreditAgreement.Config.GlobalRecurringDate.SetupType;

export type SourceType = 'reporting' | 'portCovs' | 'finCovs';

export const createStaticRecurringDateSetupShell = (): CreditAgreement.Config.GlobalRecurringDate.StaticRecurringDateSetup => {
  return {
    type: 'static',
    config: { recurringDateConfigs: [], exclusions: [] },
  };
};

export const createRelativeRecurringDateSetupShell = (params: {
  source: SourceType;
}): CreditAgreement.Config.GlobalRecurringDate.RelativeRecurringDateSetup => {
  const { source } = params;
  const defaultFormula =
    source === 'reporting' ? 'reportingDate' : 'triggerDate';
  return {
    type: 'relative',
    triggerDateFormula: defaultFormula,
    config: { relativeRecurringDateConfig: [], exclusions: [] },
  };
};

type EditRecurringDateModalProps = {
  isOpen: boolean;
  onCancel: Function;
  onSave: (config: Date.RecurringDate.Config) => void;
  recurringDateConfig: Date.RecurringDate.Config;
  displayName: string;
};
export const EditRecurringDateModal = ({
  isOpen,
  onCancel,
  onSave,
  recurringDateConfig,
  displayName,
}: EditRecurringDateModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent padding="20px" maxWidth="min-content" minWidth="3xl">
        <ModalHeader>Edit {displayName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <RecurringDateV2
            recurringDateConfig={recurringDateConfig}
            onSave={onSave}
            onCancel={onCancel}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type EditRelativeRecurringDateModalProps = {
  isOpen: boolean;
  onCancel: Function;
  onSave: Function;
  relativeDateConfig: Date.RecurringDate.RelativeConfig;
  displayName: string;
  staticStartDateFormula?: string;
  staticEndDateFormula?: string;
};
export const EditRelativeRecurringDateModal = ({
  isOpen,
  onCancel,
  onSave,
  relativeDateConfig,
  displayName,
  staticStartDateFormula,
  staticEndDateFormula,
}: EditRelativeRecurringDateModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent padding="20px" maxWidth="min-content" minWidth="3xl">
        <ModalHeader>Edit {displayName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Formik
            initialValues={cloneDeep(relativeDateConfig)}
            onSubmit={(props) => {
              onSave(props);
            }}
          >
            {(formProps: FormikProps<Date.RecurringDate.RelativeConfig>) => (
              <Form onSubmit={formProps.handleSubmit}>
                <RelativeRecurringDateForm
                  savedConfig={formProps.values}
                  setSavedConfig={formProps.setValues}
                  staticStartDateFormula={staticStartDateFormula}
                  staticEndDateFormula={staticEndDateFormula}
                  mode="setup"
                />
                <Flex padding-top="5px" justifyContent="center">
                  <Button
                    mr="5px"
                    onClick={() => {
                      formProps.submitForm();
                    }}
                  >
                    Done
                  </Button>
                  <Button onClick={() => onCancel()}>Cancel</Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const EditGlobalRecurringDateModal: React.FC<{
  onClose: () => void;
  isOpen: boolean;
  onSave: Function;
  globalRecurringDateConfig: CreditAgreement.Config.GlobalRecurringDate.Config;
  staticStartDateFormula?: string;
  staticEndDateFormula?: string;
  isSaveLoading: boolean;
  preSelectedTriggerDate?: Util.FullDateStr;
}> = ({
  onClose,
  isOpen,
  onSave,
  globalRecurringDateConfig,
  staticStartDateFormula,
  staticEndDateFormula,
  isSaveLoading,
  preSelectedTriggerDate,
}) => {
  const [useEnableDefaultValueZero, setUseEnableDefaultValueZero] = useState(
    !globalRecurringDateConfig.defaultTo.value,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent padding="20px" maxWidth="min-content" minWidth="3xl">
        <ModalHeader>
          {`Edit ${commonUtils.toTitleCase(
            globalRecurringDateConfig.setup.type,
          )} Recurring Date`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Formik
            initialValues={cloneDeep(globalRecurringDateConfig)}
            onSubmit={(props) => {
              onSave(props);
            }}
          >
            {(
              formProps: FormikProps<CreditAgreement.Config.GlobalRecurringDate.Config>,
            ) => (
              <Form onSubmit={formProps.submitForm}>
                {/* key is not editable */}
                <Text mb="8px" fontWeight={500}>
                  Key
                </Text>
                <Text>{formProps.values.field}</Text>
                {formProps.values.setup.type === 'relative' && (
                  <Box mt="24px">
                    <InputFormField
                      propName="setup.triggerDateFormula"
                      formProps={formProps}
                      label="Trigger Date Formula"
                      type="formula"
                    />
                  </Box>
                )}

                {formProps.values.setup.type === 'relative' && (
                  <RelativeRecurringDateForm
                    savedConfig={formProps.values}
                    setSavedConfig={formProps.setValues}
                    staticStartDateFormula={staticStartDateFormula}
                    staticEndDateFormula={staticEndDateFormula}
                    mode="global"
                    preSelectedTriggerDate={preSelectedTriggerDate}
                  />
                )}
                {formProps.values.setup.type === 'static' && (
                  <Box my="24px">
                    <RecurringDateV2Body
                      savedConfig={formProps.values}
                      setSavedConfig={formProps.setValues}
                      mode="global"
                    />
                  </Box>
                )}

                <Flex alignItems="center" mb="8px">
                  <Text mr="8px" fontWeight={500}>
                    Max Count
                  </Text>
                  <Tooltip
                    label='This attribute helps determine when to rely on "Default Value", in case the dynamic output is
                    actually fewer than expected'
                  >
                    <InfoOutlineIcon color={colors.info} />
                  </Tooltip>
                </Flex>

                <NumberInputFormField
                  propName="maxCount"
                  formProps={formProps}
                  inputWidth="100%"
                />

                <Flex
                  mt="16px"
                  alignItems="center"
                  onClick={() => {
                    const originalUseEnableDefaultValueZero = _.clone(
                      useEnableDefaultValueZero,
                    );
                    setUseEnableDefaultValueZero(
                      !originalUseEnableDefaultValueZero,
                    );

                    // if toggling "enable default value" from off to on
                    // set defaultTo.value to empty string
                    if (!originalUseEnableDefaultValueZero) {
                      formProps.setFieldValue('defaultTo.value', '');
                    }
                  }}
                  cursor="pointer"
                  width="fit-content"
                >
                  <ToggleIcon isActive={useEnableDefaultValueZero} />
                  <Text ml="16px" fontWeight={500}>
                    Enable Default Value: 0
                  </Text>
                </Flex>
                {!useEnableDefaultValueZero && (
                  <Box mt="24px">
                    <InputFormField
                      propName="defaultTo.value"
                      formProps={formProps}
                      label="Default Value"
                      inputWidth="100%"
                    />
                  </Box>
                )}

                <Flex justifyContent="right" mt="40px">
                  <Button
                    colorScheme="aurora"
                    variant="outline"
                    borderWidth="2px"
                    mr="32px"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="aurora"
                    color="white"
                    type="submit"
                    isLoading={isSaveLoading}
                    onClick={() => {
                      formProps.submitForm();
                    }}
                  >
                    Save
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
